/**
 * JWT Token Service
 * 
 * Сервис для управления JWT токенами с автоматическим обновлением
 * Обеспечивает непрерывную аутентификацию пользователя
 */

interface JWTServiceConfig {
  shareServerUrl: string;
  refreshIntervalMs: number;
  retryAttempts: number;
  retryDelayMs: number;
}

class JWTService {
  private config: JWTServiceConfig;
  private refreshTimer: NodeJS.Timeout | null = null;
  private isRefreshing = false;
  private retryCount = 0;
  private lastTokenSuccess = false; // Флаг успешного получения токена
  private isInitialized = false; // Флаг инициализации сервиса

  constructor(config?: Partial<JWTServiceConfig>) {
    this.config = {
      shareServerUrl: import.meta.env.VITE_SHARE_SERVER_URL || 'http://localhost:3002',
      refreshIntervalMs: 60 * 60 * 1000, // 1 час
      retryAttempts: 3,
      retryDelayMs: 5000, // 5 секунд
      ...config
    };
  }

  /**
   * Инициализация JWT сервиса (вызывается только один раз)
   * @param withRetries - использовать ли повторные попытки
   * @returns Promise<boolean> - успешность операции
   */
  async initialize(withRetries: boolean = false): Promise<boolean> {
    if (this.isInitialized) {
      console.log('Сервис токенов уже инициализирован');
      return this.lastTokenSuccess;
    }

    console.log('Инициализация сервиса токенов...');
    const success = await this.getToken(withRetries);
    this.isInitialized = true;
    return success;
  }

  /**
   * Получение JWT токена
   * @param withRetries - использовать ли повторные попытки
   * @returns Promise<boolean> - успешность операции
   */
  async getToken(withRetries: boolean = false): Promise<boolean> {
    if (withRetries) {
      return this.getTokenWithRetry();
    }

    try {
      const response = await fetch(`${this.config.shareServerUrl}/api/token`, {
        method: 'GET',
        credentials: 'include', // Важно для сохранения cookies
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        console.log('✅ токен успешно получен');
        this.retryCount = 0; // Сбрасываем счетчик попыток при успехе
        this.lastTokenSuccess = true;
        return true;
      } else {
        console.warn(`⚠️ Не удалось получить токен: ${response.status} ${response.statusText}`);
        this.lastTokenSuccess = false;
        return false;
      }
    } catch (error) {
      console.error('❌ Ошибка при получении токена:', error);
      this.lastTokenSuccess = false;
      return false;
    }
  }

  /**
   * Получение JWT токена с повторными попытками
   * @returns Promise<boolean> - успешность операции
   */
  private async getTokenWithRetry(): Promise<boolean> {
    let attempts = 0;
    const maxAttempts = this.config.retryAttempts;
    let success = false;

    while (attempts < maxAttempts && !success) {
      attempts++;
      console.log(`🔄 Попытка получения токена (${attempts}/${maxAttempts})`);

      try {
        const response = await fetch(`${this.config.shareServerUrl}/api/token`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          console.log('✅ токен успешно получен');
          this.retryCount = 0;
          this.lastTokenSuccess = true;
          success = true;
          return true;
        } else {
          console.warn(`⚠️ Не удалось получить токен: ${response.status} ${response.statusText}`);
          this.lastTokenSuccess = false;
        }
      } catch (error) {
        console.error(`❌ Ошибка при получении токена (попытка ${attempts}):`, error);
        this.lastTokenSuccess = false;
      }

      // Если это не последняя попытка, ждем перед следующей
      if (attempts < maxAttempts && !success) {
        console.log(`⏳ Повторная попытка через ${this.config.retryDelayMs / 1000} секунд...`);
        await new Promise(resolve => setTimeout(resolve, this.config.retryDelayMs));
      }
    }

    console.error(`❌ Не удалось получить токен после ${maxAttempts} попыток`);
    this.lastTokenSuccess = false;
    return false;
  }

  /**
   * Запуск автоматического обновления токена
   */
  startAutoRefresh(): void {
    if (this.refreshTimer) {
      return;
    }

    this.refreshTimer = setInterval(async () => {
      await this.refreshTokenWithRetry();
    }, this.config.refreshIntervalMs);
  }

  /**
   * Остановка автоматического обновления токена
   */
  stopAutoRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Обновление токена с повторными попытками
   */
  private async refreshTokenWithRetry(): Promise<void> {
    if (this.isRefreshing) {
      console.log('⏳ Обновление токена уже в процессе, пропускаем...');
      return;
    }

    this.isRefreshing = true;
    console.log(`🔄 Попытка обновления токена (попытка ${this.retryCount + 1}/${this.config.retryAttempts})`);

    const success = await this.getToken();

    if (success) {
      this.retryCount = 0;
    } else {
      this.retryCount++;
      
      if (this.retryCount < this.config.retryAttempts) {
        setTimeout(() => {
          this.isRefreshing = false;
          this.refreshTokenWithRetry();
        }, this.config.retryDelayMs);
        return;
      } else {
        console.error('❌ Превышено максимальное количество попыток обновления токена');
        this.retryCount = 0;
      }
    }

    this.isRefreshing = false;
  }

  /**
   * Проверка статуса сервиса
   */
  getStatus(): {
    isRunning: boolean;
    isRefreshing: boolean;
    retryCount: number;
    nextRefreshIn: number | null;
    lastTokenSuccess: boolean;
    isInitialized: boolean;
  } {
    return {
      isRunning: this.refreshTimer !== null,
      isRefreshing: this.isRefreshing,
      retryCount: this.retryCount,
      nextRefreshIn: this.refreshTimer ? this.config.refreshIntervalMs : null,
      lastTokenSuccess: this.lastTokenSuccess,
      isInitialized: this.isInitialized,
    };
  }

  /**
   * Обновление конфигурации
   */
  updateConfig(newConfig: Partial<JWTServiceConfig>): void {
    this.config = { ...this.config, ...newConfig };
    console.log('⚙️ Конфигурация сервиса обновлена:', this.config);
  }
}

// Создаем единственный экземпляр сервиса
const jwtService = new JWTService();

// Экспортируем функции для удобства использования
export const initializeJWT = (withRetries: boolean = false) => jwtService.initialize(withRetries);
export const getJWTToken = (withRetries: boolean = false) => jwtService.getToken(withRetries);
export const startJWTRefresh = () => jwtService.startAutoRefresh();
export const stopJWTRefresh = () => jwtService.stopAutoRefresh();
export const getJWTStatus = () => jwtService.getStatus();
export const updateJWTConfig = (config: Partial<JWTServiceConfig>) => jwtService.updateConfig(config);

// Экспортируем сам сервис для продвинутого использования
export default jwtService;
